import { supabase } from '../lib/supabase';

export const taskService = {
  // Daily Tasks
  async getDailyTasks(familyGroupId) {
    const { data, error } = await supabase?.from('daily_tasks')?.select(`
        *,
        assigned_member:family_members!assigned_member_id(
          id,
          display_name,
          color,
          initials,
          member_role
        )
      `)?.eq('family_group_id', familyGroupId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async createDailyTask(taskData) {
    const { data, error } = await supabase?.from('daily_tasks')?.insert(taskData)?.select(`
        *,
        assigned_member:family_members!assigned_member_id(
          id,
          display_name,
          color,
          initials,
          member_role
        )
      `)?.single();

    if (error) throw error;
    return data;
  },

  async updateDailyTask(taskId, updates) {
    const { data, error } = await supabase?.from('daily_tasks')?.update(updates)?.eq('id', taskId)?.select(`
        *,
        assigned_member:family_members!assigned_member_id(
          id,
          display_name,
          color,
          initials,
          member_role
        )
      `)?.single();

    if (error) throw error;
    return data;
  },

  async deleteDailyTask(taskId) {
    const { error } = await supabase?.from('daily_tasks')?.delete()?.eq('id', taskId);

    if (error) throw error;
  },

  // Multi-Member Tasks
  async getMultiMemberTasks(familyGroupId) {
    const { data, error } = await supabase?.from('multi_member_tasks')?.select(`
        *,
        assignments:task_assignments(
          id,
          completed,
          completed_at,
          member:family_members(
            id,
            display_name,
            color,
            initials,
            member_role
          )
        ),
        chat_messages:task_chat_messages(
          id,
          content,
          created_at,
          sender:family_members!sender_id(
            id,
            display_name,
            color,
            initials
          )
        )
      `)?.eq('family_group_id', familyGroupId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async createMultiMemberTask(taskData, assignedMemberIds) {
    // Create task
    const { data: task, error: taskError } = await supabase?.from('multi_member_tasks')?.insert(taskData)?.select()?.single();

    if (taskError) throw taskError;

    // Create assignments
    if (assignedMemberIds?.length > 0) {
      const assignments = assignedMemberIds?.map(memberId => ({
        task_id: task?.id,
        member_id: memberId
      }));

      const { error: assignError } = await supabase?.from('task_assignments')?.insert(assignments);

      if (assignError) throw assignError;
    }

    return task;
  },

  async updateTaskAssignment(taskId, memberId, completed) {
    const updates = {
      completed,
      completed_at: completed ? new Date()?.toISOString() : null
    };

    const { data, error } = await supabase?.from('task_assignments')?.update(updates)?.eq('task_id', taskId)?.eq('member_id', memberId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Recurring Tasks
  async createRecurringDailyTask(taskData, recurrenceData) {
    const recurringTask = {
      ...taskData,
      is_recurring: true,
      recurrence_frequency: recurrenceData?.frequency,
      recurrence_pattern: recurrenceData?.pattern,
      recurrence_end_date: recurrenceData?.endDate || null
    };

    const { data, error } = await supabase?.from('daily_tasks')?.insert(recurringTask)?.select(`
        *,
        assigned_member:family_members!assigned_member_id(
          id,
          display_name,
          color,
          initials,
          member_role
        )
      `)?.single();

    if (error) throw error;
    return data;
  },

  async createRecurringMultiMemberTask(taskData, assignedMemberIds, recurrenceData) {
    const recurringTask = {
      ...taskData,
      is_recurring: true,
      recurrence_frequency: recurrenceData?.frequency,
      recurrence_pattern: recurrenceData?.pattern,
      recurrence_end_date: recurrenceData?.endDate || null
    };

    const { data: task, error: taskError } = await supabase?.from('multi_member_tasks')?.insert(recurringTask)?.select()?.single();

    if (taskError) throw taskError;

    // Create assignments
    if (assignedMemberIds?.length > 0) {
      const assignments = assignedMemberIds?.map(memberId => ({
        task_id: task?.id,
        member_id: memberId
      }));

      const { error: assignError } = await supabase?.from('task_assignments')?.insert(assignments);

      if (assignError) throw assignError;
    }

    return task;
  },

  async generateRecurringInstances(parentTaskId, taskType = 'daily', daysAhead = 30) {
    // Get parent task
    const table = taskType === 'daily' ? 'daily_tasks' : 'multi_member_tasks';
    const { data: parentTask, error: fetchError } = await supabase?.from(table)?.select('*')?.eq('id', parentTaskId)?.single();

    if (fetchError) throw fetchError;

    if (!parentTask?.is_recurring) {
      throw new Error('Task is not a recurring task');
    }

    // Calculate next occurrences
    const instances = [];
    const today = new Date();
    const endDate = parentTask?.recurrence_end_date ? new Date(parentTask?.recurrence_end_date) : new Date(today?.getTime() + daysAhead * 24 * 60 * 60 * 1000);

    let currentDate = new Date(today);
    currentDate?.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      let shouldCreate = false;

      if (parentTask?.recurrence_frequency === 'daily') {
        shouldCreate = true;
      } else if (parentTask?.recurrence_frequency === 'weekly') {
        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']?.[currentDate?.getDay()];
        shouldCreate = parentTask?.recurrence_pattern?.daysOfWeek?.includes(dayName);
      } else if (parentTask?.recurrence_frequency === 'monthly') {
        shouldCreate = currentDate?.getDate() === parentTask?.recurrence_pattern?.dayOfMonth;
      }

      if (shouldCreate) {
        instances?.push({
          date: new Date(currentDate),
          parentTaskId
        });
      }

      // Move to next day
      currentDate?.setDate(currentDate?.getDate() + 1);
    }

    return instances;
  },

  async createTaskInstance(parentTaskId, occurrenceDate, taskType = 'daily') {
    const functionName = taskType === 'daily' ?'create_daily_task_instance' :'create_multi_member_task_instance';

    const { data, error } = await supabase?.rpc(functionName, {
      parent_id: parentTaskId,
      occurrence_date: occurrenceDate
    });

    if (error) throw error;
    return data;
  },

  // Chat Messages
  async sendChatMessage(taskId, senderId, content) {
    const { data, error } = await supabase?.from('task_chat_messages')?.insert({
        task_id: taskId,
        sender_id: senderId,
        content
      })?.select(`
        *,
        sender:family_members!sender_id(
          id,
          display_name,
          color,
          initials
        )
      `)?.single();

    if (error) throw error;
    return data;
  },

  // Real-time subscriptions
  subscribeToDailyTasks(familyGroupId, callback) {
    const channel = supabase?.channel('daily_tasks_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'daily_tasks',
          filter: `family_group_id=eq.${familyGroupId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  subscribeToMultiMemberTasks(familyGroupId, callback) {
    const channel = supabase?.channel('multi_member_tasks_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'multi_member_tasks',
          filter: `family_group_id=eq.${familyGroupId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  subscribeToTaskAssignments(taskId, callback) {
    const channel = supabase?.channel(`task_assignments_${taskId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_assignments',
          filter: `task_id=eq.${taskId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  subscribeToChatMessages(taskId, callback) {
    const channel = supabase?.channel(`chat_messages_${taskId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'task_chat_messages',
          filter: `task_id=eq.${taskId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    return supabase?.removeChannel(channel);
  }
};